import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import NotFound from "./NotFound";
import { Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import EnquiryForm from "../components/EnquiryForm";
import SectionHeader from "../components/SectionHeader";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { apartments, accommodationConditions } from "../data/site";
import { publicApi, fileUrl } from "../lib/adminApi";

export default function ApartmentDetail() {
  const { slug } = useParams();
  const apt = apartments.find((a) => a.slug === slug);
  const [uploaded, setUploaded] = useState([]);
  const [mainImg, setMainImg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!apt) return;
    // Reset the previous apartment's photos immediately when the slug changes
    // so the visitor never sees stale content while the fetch is in flight.
    setUploaded([]);
    setMainImg(null);
    publicApi.get(`/photos?target_type=apartment&target_slug=${slug}`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.data.photos || [])
          .filter((p) => p && p.id)
          .map((p) => ({ id: p.id, src: fileUrl(p.id), alt: p.alt || apt.name }))
          .filter((it) => typeof it.src === "string" && it.src.length > 0);
        setUploaded(list);
        setMainImg(list[0] || null);
      })
      .catch(() => { if (!cancelled) { setUploaded([]); setMainImg(null); } });
    return () => { cancelled = true; };
  }, [slug, apt]);

  const gallery = uploaded;
  if (!apt) return <NotFound />;
  const c = accommodationConditions;
  const activeSrc = mainImg && mainImg.src;

  return (
    <main id="main">
      <Seo path={`/ubytovani/${slug}`} title={`${apt.name} – ubytování u Lednice | Ubytování u vinaře`} description={apt.summary} />
      <Breadcrumbs items={[{ label: "Ubytování", to: "/ubytovani" }, { label: apt.name }]} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="w-full aspect-[4/3] bg-cream">
            {activeSrc && (
              <img src={activeSrc} alt={mainImg?.alt || apt.name} className="w-full h-full object-cover" loading="lazy" onError={() => setMainImg(null)} />
            )}
          </div>
          {gallery.length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((g) => (
                <button key={g.id} onClick={() => setMainImg(g)} className={`aspect-[4/3] overflow-hidden border-2 bg-cream ${activeSrc === g.src ? "border-burgundy" : "border-transparent"}`}>
                  <img src={g.src} alt={g.alt || ""} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          <div className="mt-6"><AvailabilityCalendar slug={apt.slug} /></div>
        </div>
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[.28em] uppercase text-burgundy mb-3">Apartmán</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl leading-tight">{apt.name}</h1>
          <p className="mt-4 text-charcoal/80 leading-relaxed">{apt.summary}</p>

          <div className="mt-8 grid grid-cols-2 gap-5">
            <Info label="Kapacita" value={apt.capacity} />
            <Info label="Min. délka pobytu" value={`${apt.minNights} noci`} />
            <Info label="Cena mimo zimu" value={`${c.perPersonSummer} Kč / osoba / noc`} />
            <Info label="Listopad–březen" value={`${c.perPersonWinter} Kč / osoba / noc`} />
          </div>

          {apt.equipment.length > 0 && (
            <div className="mt-8">
              <h2 className="font-serif text-2xl mb-3">Vybavení</h2>
              <ul className="grid grid-cols-2 gap-y-1 text-sm text-charcoal/80">
                {apt.equipment.map((e, i) => <li key={i} className="flex gap-2"><Check size={14} className="text-gold mt-1" /> {e}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-8 border-t border-charcoal/15 pt-6 text-sm text-charcoal/75 space-y-1.5">
            <div>• Check-in {c.checkIn} • Check-out {c.checkOut}</div>
            <div>• {c.parking} {c.pets}</div>
            <div>• {c.bicycleStorage}</div>
            <div>• Místní poplatek: {c.localFee}</div>
            <div>• {c.deposit}</div>
          </div>

          <a href="#poptavka" className="mt-8 inline-block bg-burgundy hover:bg-burgundy-hover text-ivory px-8 py-3.5 tracking-wide">Poptat tento apartmán</a>
        </div>
      </section>

      <section id="poptavka" className="bg-cream py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Poptávka pobytu" title={`Volný termín – ${apt.name}`} />
          <div className="mt-8 bg-ivory p-6 md:p-10 border border-charcoal/10">
            <EnquiryForm key={apt.slug} defaultType="ubytovani" defaultApartment={apt.slug} />
          </div>
        </div>
      </section>
    </main>
  );
}

function Info({ label, value }) {
  return (
    <div className="border-t border-charcoal/15 pt-3">
      <div className="text-[10px] tracking-[.22em] uppercase text-charcoal/50">{label}</div>
      <div className="font-serif text-base text-charcoal mt-1">{value}</div>
    </div>
  );
}
